/**
 * CR-5257 + CR-5258 — Operational Status KPIs.
 *
 * WIP / On Hold cards on every dashboard. Overs (>15 working days) and
 * Priority cards are gated server-side and only render when the data
 * payload provides them (Poeton-internal / helix portal mode).
 */
import { Activity, AlertTriangle, Clock, Flag } from 'lucide-react';
import { useFormatters } from '../../contexts/PreferencesContext';

interface OperationalKPI {
  count: number;
  value: number;
}

interface OperationalKPIsSectionProps {
  operational?: {
    wip: OperationalKPI;
    on_hold: OperationalKPI;
    overs_15d: OperationalKPI | null;
    priority: OperationalKPI | null;
  };
  currency: string;
}

export function OperationalKPIsSection({ operational, currency }: OperationalKPIsSectionProps) {
  const { formatCurrency } = useFormatters();
  if (!operational) return null;

  const cards: Array<{
    key: string;
    label: string;
    count: number;
    value: number;
    Icon: typeof Activity;
    accent: string;
  }> = [
    { key: 'wip', label: 'WIP', count: operational.wip.count, value: operational.wip.value, Icon: Activity, accent: 'text-sky-600' },
    { key: 'on_hold', label: 'On Hold', count: operational.on_hold.count, value: operational.on_hold.value, Icon: Clock, accent: 'text-amber-600' },
  ];
  if (operational.overs_15d) {
    cards.push({
      key: 'overs',
      label: 'Overs > 15 days',
      count: operational.overs_15d.count,
      value: operational.overs_15d.value,
      Icon: AlertTriangle,
      accent: 'text-rose-600',
    });
  }
  if (operational.priority) {
    cards.push({
      key: 'priority',
      label: 'Priority',
      count: operational.priority.count,
      value: operational.priority.value,
      Icon: Flag,
      accent: 'text-purple-600',
    });
  }

  return (
    <div className="bg-card rounded-lg shadow-sm p-4" data-tour="dashboard-operational-kpis">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3 px-1">
        Operational Status
      </h3>
      <div className={`grid gap-4 grid-cols-2 md:grid-cols-${cards.length}`}>
        {cards.map(({ key, label, count, value, Icon, accent }) => (
          <div
            key={key}
            className="border border-border rounded-lg p-4 flex flex-col items-start bg-muted/20"
          >
            <div className="flex items-center gap-2 mb-2">
              <Icon className={`h-4 w-4 ${accent}`} />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {label}
              </span>
            </div>
            <div className="text-3xl font-bold text-foreground leading-none mb-1">
              {count.toLocaleString()}
            </div>
            <div className="text-xs text-muted-foreground">
              {value > 0 ? formatCurrency(value, currency) : '—'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
